import React, { lazy } from 'react';
import ReactDOM from 'react-dom/client';
import {
    createBrowserRouter,
    createRoutesFromElements,
    Route,
    RouterProvider,
    Navigate } from "react-router-dom";
import { Provider } from 'react-redux';
import store from "./store";
import "./assets/styles/bootstrap.custom.css";
import "./assets/styles/styles.css";
import App from './App';
import reportWebVitals from './reportWebVitals';
import About from "./screens/About";
import Help from "./screens/Help";
import HomeScreen from "./screens/HomeScreen";
import ProductScreen from "./screens/ProductScreen";
import CartScreen from "./screens/CartScreen";
import LoginScreen from "./screens/LoginScreen"
import RegisterScreen from "./screens/RegisterScreen";
import CheckoutScreen from "./screens/CheckoutScreen";
import CheckoutCompleteScreen from "./screens/CheckoutCompleteScreen";
import PrivateRoute from "./components/PrivateRoute";
import AdminRoute from "./components/AdminRoute";
import OrderScreen from "./screens/OrderScreen";
import ProfileScreen from "./screens/ProfileScreen";
import ProfileOrderScreen from "./screens/ProfileOrderScreen";
import AccountDetailsScreen from "./screens/AccountDetailsScreen";
import WishlistScreen from "./screens/WishlistScreen";
import { HelmetProvider } from "react-helmet-async";
import Contact from "./screens/Contact";
import PasswordResetScreen from "./screens/PasswordReset";
import UserPasswordReset from "./screens/UserPasswordReset";
import Faq from "./screens/Faq";
import VerifyEmailScreen from "./screens/VerifyEmailScreen";
import ResendVerificationScreen from "./screens/ResendVerificationScreen";
import LandingPage from "./screens/LandingPage";
import GiftCategoryPage from "./screens/GiftCategoryPage";
import UnsubscribePage from './screens/UnsubscribePage';
import GoogleAuthSuccessScreen from "./screens/GoogleAuthSuccessScreen";
import * as Sentry from "@sentry/react";
import NotFoundScreen from "./screens/NotFoundScreen";

// Loaded on demand so shoppers do not download admin and legal pages
const OrderListScreen = lazy(() => import("./screens/admin/OrderListScreen"));
const ProductListScreen = lazy(() => import("./screens/admin/ProductListScreen"));
const ProductEditScreen = lazy(() => import("./screens/admin/ProductEditScreen"));
const UserListScreen = lazy(() => import("./screens/admin/UserListScreen"));
const UserEditScreen = lazy(() => import("./screens/admin/UserEditScreen"));
const Terms = lazy(() => import("./screens/Terms"));
const Cookie = lazy(() => import("./screens/Cookie"));
const Accessibility = lazy(() => import("./screens/Accessibility"));
const Privacy = lazy(() => import("./screens/Privacy"));
const ProductCreationWizard = lazy(() => import("./screens/admin/ProductCreationWizard"));
const PromoCodeListScreen = lazy(() => import("./screens/admin/PromoCodeListScreen"));
const PromoCodeEditScreen = lazy(() => import("./screens/admin/PromoCodeEditScreen"));
const FaireImportScreen = lazy(() => import("./screens/admin/FaireImportScreen"));
const StockUpdateScreen = lazy(() => import("./screens/admin/StockUpdateScreen"));

Sentry.init({
    dsn: "https://44ba98b84bb88d1076a362b52a717285@o4510770966364160.ingest.de.sentry.io/4510770969444432",
    environment: process.env.NODE_ENV,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
});

const router = createBrowserRouter(
    createRoutesFromElements(
        <Route path="/" element={<App />}>
            {/* Public routes */}
            <Route index={true} element={<LandingPage />} />
            <Route path="/" element={<HomeScreen />} />
            <Route path="/search/:keyword?" element={<HomeScreen />} />
            <Route path="/search/:keywordDescription" element={<HomeScreen />} />
            <Route path="/page/:pageNumber" element={<HomeScreen />} />
            <Route path="/search/:keyword/page/:pageNumber" element={<HomeScreen />} />
            <Route path='/product/:id' element={<ProductScreen />} />
            <Route path='/gifts/:category' element={<GiftCategoryPage />} />
            <Route path='/cart' element={<CartScreen />} />
            <Route path='/login' element={<LoginScreen />} />
            <Route path='/register' element={<RegisterScreen />} />
            <Route path='/auth/google/success' element={<GoogleAuthSuccessScreen />} />
            <Route path='/about' element={<About />} />
            <Route path='/terms' element={<Terms />} />
            <Route path='/privacy' element={<Privacy />} />
            <Route path='/help' element={<Help />} />
            <Route path='/cookie' element={<Cookie />} />
            <Route path='/faqs' element={<Faq />} />
            <Route path='/contact' element={<Contact/>} />
            <Route path='/accessibility' element={<Accessibility />} />
            <Route path='/passwordreset' element={<PasswordResetScreen />} />
            <Route path='/reset/:token' element={<UserPasswordReset />} />
            <Route path='/verify-email' element={<VerifyEmailScreen />} />
            <Route path='/resend-verification' element={<ResendVerificationScreen />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/product/:slug" element={<ProductScreen />} />

            {/* Checkout routes — accessible to guests and logged-in users */}
            <Route path='/checkout' element={<CheckoutScreen />} />
            <Route path='/checkout/complete' element={<CheckoutCompleteScreen />} />
            {/* Old multi-step checkout URLs → the one-page checkout */}
            <Route path='/shipping' element={<Navigate to='/checkout' replace />} />
            <Route path='/payment' element={<Navigate to='/checkout' replace />} />
            <Route path='/placeorder' element={<Navigate to='/checkout' replace />} />
            <Route path='/order/:id' element={<OrderScreen />} />

            {/* Private routes — logged-in users only */}
            <Route path='' element={<PrivateRoute />}>
                <Route path='/profile' element={<ProfileScreen />} />
                <Route path='/profile/details' element={<AccountDetailsScreen />} />
                <Route path='/wishlist' element={<WishlistScreen />} />
                <Route path='/orders' element={<ProfileOrderScreen />} />
            </Route>

            {/* Admin routes */}
            <Route path='' element={<AdminRoute />}>
                <Route path='/admin/orders/user/:userId' element={<OrderListScreen />}/>
                <Route path='/admin/orderlist' element={<OrderListScreen />} />
                <Route path='/admin/productlist' element={<ProductListScreen />} />
                <Route path='/admin/productlist/page/:pageNumber' element={<ProductListScreen />} />
                <Route path='/admin/product/create' element={<ProductCreationWizard />} />
                <Route path='/admin/product/:id/edit' element={<ProductEditScreen />} />
                <Route path='/admin/userlist' element={<UserListScreen />} />
                <Route path='/admin/user/:id/edit' element={<UserEditScreen />} />
                <Route path='/admin/promocodelist' element={<PromoCodeListScreen />} />
                <Route path='/admin/promocode/create' element={<PromoCodeEditScreen />} />
                <Route path='/admin/promocode/:id/edit' element={<PromoCodeEditScreen />} />
                <Route path="/admin/faire-import" element={<FaireImportScreen />} />
                <Route path="/admin/stock-updates" element={<StockUpdateScreen />} />
            </Route>

            <Route path="*" element={<NotFoundScreen />} />
        </Route>
    )
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <React.StrictMode>
        <HelmetProvider>
            <Sentry.ErrorBoundary
                fallback={({ error, resetError }) => (
                    <div className="container mt-5">
                        <div className="alert alert-danger">
                            <h4>Oops! Something went wrong</h4>
                            <p>We've been notified and are working on it. Please try refreshing the page.</p>
                            <button className="btn btn-primary mt-2" onClick={resetError}>
                                Refresh Page
                            </button>
                        </div>
                    </div>
                )}
                showDialog
            >
                <Provider store={store}>
                    <RouterProvider router={router} />
                </Provider>
            </Sentry.ErrorBoundary>
        </HelmetProvider>
    </React.StrictMode>
);

reportWebVitals();